import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  ConfirmDialog,
  EmptyState,
  FormField,
  LoadingState,
  Modal,
  TextAreaField,
  Toast,
} from './ui';

const PrecursorsList = ({ beltId }) => {
  const [precursors, setPrecursors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [formData, setFormData] = useState({ name: '', biography: '', photo_url: '' });
  const [toast, setToast] = useState({ open: false, message: '', tone: 'success' });

  const showToast = (message, tone = 'success') => setToast({ open: true, message, tone });

  const fetchPrecursors = async () => {
    try {
      const response = await api.get(`/admin/belts/${beltId}/precursors`);
      setPrecursors(response.data.data);
    } catch (error) {
      showToast('Error al cargar precursores', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrecursors();
  }, [beltId]);

  const openDialog = (item = null) => {
    setSelected(item);
    setFormData(
      item
        ? { name: item.name || '', biography: item.biography || '', photo_url: item.photo_url || '' }
        : { name: '', biography: '', photo_url: '' }
    );
    setDialogOpen(true);
  };

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.biography.trim()) {
      showToast('El nombre y la biografía son obligatorios', 'error');
      return;
    }
    try {
      if (selected) {
        await api.put(`/admin/belts/${beltId}/precursors/${selected.id}`, formData);
        showToast('Precursor actualizado');
      } else {
        if (precursors.length >= 10) {
          showToast('Límite de precursores alcanzado (máximo 10)', 'error');
          return;
        }
        await api.post(`/admin/belts/${beltId}/precursors`, formData);
        showToast('Precursor creado');
      }
      setDialogOpen(false);
      fetchPrecursors();
    } catch (error) {
      showToast(error.response?.data?.message || 'Error al guardar precursor', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/belts/${beltId}/precursors/${confirmId}`);
      setConfirmId(null);
      showToast('Precursor eliminado');
      fetchPrecursors();
    } catch (error) {
      showToast('Error al eliminar precursor', 'error');
    }
  };

  const move = async (id, dir) => {
    try {
      await api.put(`/admin/belts/${beltId}/precursors/${id}/move-${dir}`);
      fetchPrecursors();
    } catch (error) {
      showToast('Error al mover precursor', 'error');
    }
  };

  if (loading) return <LoadingState label="Cargando precursores…" />;

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">Precursores ({precursors.length}/10)</h2>
        <button type="button" className="btn-admin" onClick={() => openDialog()} disabled={precursors.length >= 10}>
          Agregar
        </button>
      </div>

      {precursors.length === 0 ? (
        <EmptyState title="Sin precursores" />
      ) : (
        <ul className="space-y-3">
          {precursors.map((p, index) => (
            <li key={p.id} className="rounded-lg border border-line bg-surface p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                <div className="flex min-w-0 flex-1 gap-3">
                  {p.photo_url ? (
                    <img src={p.photo_url} alt="" className="h-12 w-12 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                      {p.name?.[0] || '?'}
                    </span>
                  )}
                  <div>
                    <h3 className="font-semibold">{p.name}</h3>
                    <p className="mt-1 line-clamp-3 text-sm text-muted whitespace-pre-line">{p.biography}</p>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-1">
                  <button type="button" className="btn-admin-ghost !min-h-9" disabled={index === 0} onClick={() => move(p.id, 'up')}>↑</button>
                  <button type="button" className="btn-admin-ghost !min-h-9" disabled={index === precursors.length - 1} onClick={() => move(p.id, 'down')}>↓</button>
                  <button type="button" className="btn-admin-ghost !min-h-9" onClick={() => openDialog(p)}>Editar</button>
                  <button type="button" className="btn-admin-ghost !min-h-9 text-danger" onClick={() => setConfirmId(p.id)}>Eliminar</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={dialogOpen}
        title={selected ? 'Editar precursor' : 'Agregar precursor'}
        onClose={() => setDialogOpen(false)}
        size="lg"
        footer={
          <>
            <button type="button" className="btn-admin-secondary" onClick={() => setDialogOpen(false)}>Cancelar</button>
            <button type="submit" form="precursor-form" className="btn-admin">{selected ? 'Actualizar' : 'Crear'}</button>
          </>
        }
      >
        <form id="precursor-form" className="space-y-4" onSubmit={handleSubmit}>
          <FormField id="name" label="Nombre" value={formData.name} onChange={handleChange} required />
          <TextAreaField id="biography" label="Biografía" value={formData.biography} onChange={handleChange} required rows={6} />
          <FormField id="photo_url" label="URL de foto" value={formData.photo_url} onChange={handleChange} />
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmId)}
        title="Eliminar precursor"
        message="¿Estás seguro de eliminar este precursor?"
        confirmLabel="Eliminar"
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmId(null)}
      />

      <Toast open={toast.open} message={toast.message} tone={toast.tone} onClose={() => setToast((t) => ({ ...t, open: false }))} />
    </div>
  );
};

export default PrecursorsList;
